import React, {useState, useEffect} from 'react';
import {Tabs} from 'antd';
import {ModelDTO, ModelDetailDTO, UserType} from 'hemwb-api';
import {OverviewTab} from './OverviewTab';
import {ModelFilesTab} from './ModelFilesTab';
import {ModelFilesTabTH} from './ModelFilesTabTH';
import {SupportingDocumentsTab} from './SupportingDocumentsTab';
import {NotesAndLinksTab} from './NotesAndLinksTab';
import {isModelTheorem} from '../../../../store/model';
import {canViewModelFolders, checkExternalSecurityAPI} from '../../../../permissions/usePermissions';
import {PERMISSION} from '../../../../permissions/types';
import {useCurrentUser} from '../../../../store/auth';
import styles from './ModelDetailTabs.module.scss';

interface ModelDetailTabsProps {
  model: ModelDTO | null;
  aiSummary: string | undefined;
  enableRequestChange: boolean | undefined;
  currentModelUuid: string;
  familyModels: (ModelDTO & {children?: ModelDTO[]})[] | undefined;
  /** HEMT-8938: true when model owner opted out of AI summary */
  aiSummaryOptedOut?: boolean;
  /** HEMT-9520: Status of AI summary generation (PENDING/READY/FAILED) */
  aiSummaryStatus?: string;
  /** HEMT-8938: true when current user is the model entry owner */
  isOwner?: boolean;
  /** HEMT-8938: true when the model has a Model Development Plan — used to distinguish
   * 'generating' state from 'no MDP' state on first page load before async job completes */
  hasMdp?: boolean;
  /** HEMT-9520: true when the model has at least one Technical Report file */
  hasTechnicalReport?: boolean;
}

/**
 * Tab container for ModelDetail page
 * Three tabs: Overview, Model Files, Supporting Documents (Theorem models only)
 * Model Files and Supporting Documents tabs are disabled when user lacks permission
 *
 * Tab Disable Logic (matches ModelOverviewTH):
 * - Regular models: disabled = !canViewModelFolders(model)
 * - THEOREM models: disabled = !canViewModelFolders(model) && !checkExternalSecurityAPI(modelDetail)
 * - Super Users on THEOREM: Always enabled (can manage file access)
 */
export const ModelDetailTabs: React.FC<ModelDetailTabsProps> = ({
  model,
  aiSummary,
  enableRequestChange,
  currentModelUuid,
  familyModels,
  aiSummaryOptedOut = false,
  aiSummaryStatus = undefined,
  isOwner = false,
  hasMdp = false,
  hasTechnicalReport = false,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const {userType} = useCurrentUser();
  const isExternalUser = userType === UserType.EXTERNALUSER;
  const isTheoremModel = isModelTheorem(model);

  // Read tab from URL query parameter on mount
  // Valid tabs: 'overview', 'folder', 'documents', 'notes'
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const tabParam = queryParams.get('tab');

    if (tabParam && ['overview', 'folder', 'documents', 'notes'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  // Calculate tab disabled state (matches ModelOverviewTH logic)
  let tabsDisabled = false;

  if (isTheoremModel) {
    // THEOREM models: Check both canViewModelFolders AND checkExternalSecurityAPI
    // Disabled only if BOTH are false (using &&)
    const hasViewPermission = canViewModelFolders(model || undefined) === PERMISSION.ALLOWED;
    const hasExternalAccess = checkExternalSecurityAPI(model as ModelDetailDTO) === PERMISSION.ALLOWED;
    tabsDisabled = !hasViewPermission && !hasExternalAccess;

    // Super Users can always access THEOREM tabs (to manage file access)
    if (userType === UserType.SUPERUSER) {
      tabsDisabled = false;
    }
  } else {
    // Regular models: Only check canViewModelFolders
    tabsDisabled = canViewModelFolders(model || undefined) !== PERMISSION.ALLOWED;
  }

  // Check if Notes and Links tab should be disabled (no data in either field)
  const hasNotesOrLinks = !!(model?.metadata?.notes?.trim() || model?.metadata?.training_and_publication_link?.trim());
  const notesAndLinksDisabled = !hasNotesOrLinks;

  const items = [
    {
      key: 'overview',
      label: <span data-tour-id="model-detail-tab-overview">Overview</span>,
      children: (
        <div data-tour-id="model-detail-overview-content">
          <OverviewTab
            aiSummary={aiSummary}
            enableRequestChange={enableRequestChange}
            currentModelUuid={currentModelUuid}
            familyModels={familyModels}
            isExternalUser={isExternalUser}
            aiSummaryOptedOut={aiSummaryOptedOut}
            aiSummaryStatus={aiSummaryStatus}
            isOwner={isOwner}
            hasMdp={hasMdp}
            hasTechnicalReport={hasTechnicalReport}
          />
        </div>
      ),
    },
    {
      key: 'folder',
      label: <span data-tour-id="model-detail-tab-files">Model Files</span>,
      children: (
        <div data-tour-id="model-detail-files-content">
          {isTheoremModel ? <ModelFilesTabTH model={model} /> : <ModelFilesTab model={model} />}
        </div>
      ),
      disabled: tabsDisabled,
    },
    // Only show Supporting Documents tab for Theorem models
    ...(isTheoremModel
      ? [
          {
            key: 'documents',
            label: 'Supporting Documents',
            children: <SupportingDocumentsTab model={model} />,
            disabled: tabsDisabled,
          },
        ]
      : []),
    {
      key: 'notes',
      label: 'Notes and Links',
      children: <NotesAndLinksTab model={model} />,
      disabled: notesAndLinksDisabled,
    },
  ];

  return (
    <div className={styles.tabsContainer} data-tour-id="model-detail-tabs">
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} type="card" />
    </div>
  );
};
